import React, { useEffect, useRef } from "react";
import { trackAdImpression, trackAdClick } from "../lib/analytics";

const AD_CLIENT = "ca-pub-0647251616275542";

/**
 * <AdSlot slot="1234567890" name="landing-mid" format="auto" />
 *
 * - If `slot` is missing/placeholder, we render a silent container — Google
 *   "Auto Ads" will still place ads on the page via the head script.
 * - When a real slot is provided, we push an adsbygoogle ad and count the
 *   impression once the container enters the viewport.
 */
export function AdSlot({
  slot,
  name = "ad",
  format = "auto",
  layout,
  fullWidthResponsive = true,
  minHeight = 120,
  className = "",
  testid,
}) {
  const containerRef = useRef(null);
  const pushedRef = useRef(false);

  const hasSlot = slot && !String(slot).toLowerCase().includes("xxx");

  useEffect(() => {
    if (!hasSlot) return;
    if (pushedRef.current) return;
    pushedRef.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // AdSense not loaded (blocker or still loading) — fail silent
    }
  }, [hasSlot]);

  // Analytics: fire impression once visible
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            trackAdImpression(name, { slot_id: slot || null });
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [name, slot]);

  const onClick = () => trackAdClick(name, { slot_id: slot || null });

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      data-testid={testid || `ad-slot-${name}`}
      className={`w-full max-w-full overflow-hidden ${className}`}
      style={{ minHeight }}
      aria-label="Advertisement"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1 text-center opacity-60">
        Ad
      </div>
      {hasSlot ? (
        <ins
          className="adsbygoogle block w-full"
          style={{ display: "block", minHeight }}
          data-ad-client={AD_CLIENT}
          data-ad-slot={slot}
          data-ad-format={format}
          data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
          {...(layout ? { "data-ad-layout": layout } : {})}
        />
      ) : (
        /* Auto-Ads fallback: invisible anchor; Auto Ads from head script will inject where appropriate */
        <div className="w-full" style={{ minHeight }} />
      )}
    </div>
  );
}
